import { useQueries } from "@tanstack/react-query";
import _ from "lodash";
import PropType from "prop-types";
import React, { useMemo } from "react";
import { Loader, Segment } from "semantic-ui-react";
import { getAnalysisResultsForSummaryTableAsync } from "../../../../apis/analysisApi";
import { useAuth0 } from "../../../../auth/auth0";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import { useUserAuth } from "../../../../hooks/useUserAuth";
import * as tableShader from "../../../Lumber/TableShader";
import { useTrialAnalysis } from "../../Trials/TrialAnalysis/TrialAnalysisContext";
import SummaryTableResults from "./summaryTableResults";
import SummaryTableSelections from "./summaryTableSelections";

const SummaryTable = ({ rawTrialOptions }) => {
	const DATE_FORMAT = "M/D/YYYY";

	const { getTokenSilently } = useAuth0();
	const userAuth = useUserAuth();
	const moduleNavigation = useModuleNavigation();

	const { committed, display, isDirty, isInitialized } = useTrialAnalysis();

	const { data: queryResults, isFetching } = useQueries({
		queries: _.map(committed.summaryTableSelections, (analysis) => ({
			queryKey: [
				"analysis data trial summary table",
				userAuth.currentClientId,
				moduleNavigation.trialId,
				analysis,
				committed.meanComparisonOptionId,
				committed.alphaOptionId,
				committed.removeDamagedOrExcluded,
				committed.removeOutliers,
				committed.outlierType,
			],
			queryFn: async () =>
				getAnalysisResultsForSummaryTableAsync(
					userAuth.currentClientId,
					moduleNavigation.trialId,
					analysis.isGrowthCurveAnalysis ? analysis.analysisId : analysis.isAnalysis ? analysis.analysisValue : null,
					analysis.quantifiedRegionTypeOptionId,
					analysis.analysisTypeId,
					analysis.isGrowthCurveAnalysis ? null : analysis.isAnalysis ? null : analysis.analysisValue,
					analysis.isGrowthCurveAnalysis ? analysis.curveModelAnalysisId : null,
					committed.meanComparisonOptionId,
					committed.alphaOptionId,
					await getTokenSilently(),
					committed.removeDamagedOrExcluded,
					committed.removeOutliers,
					committed.outlierType,
				),
			enabled: !isDirty && isInitialized && !!userAuth.currentClientId && !!moduleNavigation.trialId,
		})),
		combine: (queries) => ({
			data: _.chain(queries)
				.map((q) =>
					display.experimentalDesignId ? q.data?.data[display.experimentalDesignId] : _.values(q.data?.data)?.[0],
				)
				.filter((data) => data?.length > 0)
				.value(),
			isFetching: _.some(queries, (q) => q.isFetching),
		}),
	});

	// Mutates data in-place: sets shading, normalizes mean comparison letters, calculates stand count mean
	function setupTreatmentData(r) {
		const isAnalysisType = r[0].analysisName;
		const isGrowthCurveType = r[0].curveModelName;

		if (isAnalysisType === "Stand Count (LAI)" || isAnalysisType === "Stand Count (LAI_M3M)") {
			const mean = _.meanBy(r, "standCountAnalysisResultValue");
			_.forEach(r, (value) => {
				value.mean = mean;
			});
		}

		const treatmentsByAssessment = isGrowthCurveType
			? _.groupBy(r, "curveModelAnalysisId")
			: isAnalysisType
				? _.groupBy(r, "flightId")
				: _.groupBy(r, "groundDatasetId");

		_.forEach(treatmentsByAssessment, (treatments) => {
			const orderedList = _.orderBy(treatments, ["trialResultAverageValue"], ["desc"]);

			const treatmentsByType = isGrowthCurveType
				? _.groupBy(r, "curveModelAnalysisId")
				: isAnalysisType
					? _.groupBy(orderedList, "analysisId")
					: _.groupBy(orderedList, "groundDataAssessmentId");

			_.forEach(treatmentsByType, (analysis) => {
				const allLetterA = _.every(analysis, (t) => !t.letter || t.letter === "a");
				if (allLetterA) {
					_.forEach(analysis, (t) => {
						t.letter = "-";
					});
				}
			});

			_.forEach(treatmentsByType, (treatment) => {
				const treatmentsToShade = treatment.filter((t) => !t.hasNoPlotData);
				tableShader.calculateShadingForTreatment(treatmentsToShade, "trialResultAverageValue", "87, 184, 120");
			});
		});
	}

	function groupTreatments(res) {
		const ordered = _.orderBy(res, ["trialTreatmentId"], ["asc"]);
		let groupedTreatments = _.groupBy(ordered, "trialTreatmentId");

		const biggestLength = Math.max(..._.map(groupedTreatments, (el) => el.length));
		const biggestTreatment = _.find(groupedTreatments, (gt) => gt.length === biggestLength);
		const incomplete = _.filter(groupedTreatments, (gt) => gt.length !== biggestLength);

		_.forEach(incomplete, (it) => {
			_.forEach(biggestTreatment, (b, i) => {
				const exists =
					_.findIndex(it, (ia) => {
						if (ia.curveModelAnalysisId)
							return ia.curveModelAnalysisId === b.curveModelAnalysisId && ia.analysisId === b.analysisId;
						if (ia.groundDataAssessmentId)
							return ia.groundDataAssessmentId === b.groundDataAssessmentId && ia.dateCollected === b.dateCollected;
						if (ia.analysisId) return ia.analysisId === b.analysisId && ia.flightDate === b.flightDate;
					}) !== -1;

				if (!exists) it.splice(i, 0, { ...b, hasNoPlotData: true });
			});
		});

		return _.map(groupedTreatments, (gt) =>
			gt.curveModelName
				? _.orderBy(gt, ["curveModelName"], ["asc"])
				: gt.name
					? _.orderBy(gt, ["analysisName"], ["asc"])
					: _.orderBy(gt, ["groundDataAssessmentName"], ["asc"]),
		);
	}

	function buildFooterData(groupedTreatments) {
		const parse = groupedTreatments[0];
		return [
			_.map(parse, (t) => ({ ...t, footerName: "Global Mean" })),
			_.map(parse, (t) => ({ ...t, footerName: "CV" })),
			_.map(parse, (t) => ({ ...t, footerName: "P-Value" })),
		];
	}

	const trialDataResults = useMemo(() => {
		if (isFetching || queryResults.length === 0) return [];
		// setupTreatmentData must run per selection — it reads r[0].analysisName
		// to determine the data type and set up shading for that type
		queryResults.forEach((data) => setupTreatmentData(data));

		const sorted = _.sortBy(_.flatten(queryResults), ["flightDate", "dateCollected"]);
		const grouped = groupTreatments(sorted);
		return grouped;
	}, [isFetching, queryResults]);

	const footerData = useMemo(() => {
		if (isFetching || trialDataResults.length === 0) return [];
		return buildFooterData(trialDataResults);
	}, [isFetching, trialDataResults]);

	return !rawTrialOptions ? (
		<Segment basic style={{ width: "100%", height: "100%" }}>
			<Loader active inline="centered" />
		</Segment>
	) : (
		<Segment basic style={{ width: "calc(100% + 2em)", overflowX: "scroll", margin: "-1em", height: "100%" }}>
			<SummaryTableSelections
				fetchingData={isFetching}
				rawTrialOptions={rawTrialOptions}
				rawPresetOptions={rawTrialOptions.presetOptions}
			/>
			{isFetching ? (
				<Loader active inline="centered" />
			) : (
				<div className="aucTableContainer">
					<SummaryTableResults
						trialDataResults={trialDataResults}
						summaryTableSelections={committed.summaryTableSelections}
						DATE_FORMAT={DATE_FORMAT}
						footerData={footerData}
						selectedPresetOption={_.find(
							rawTrialOptions.summaryTablePresets,
							(stp) => stp.id === committed.summaryTablePresetId,
						)}
						needToUpdateAnalytics={isDirty}
					/>
				</div>
			)}
		</Segment>
	);
};

SummaryTable.propTypes = {
	rawTrialOptions: PropType.object,
};

export default SummaryTable;
